<template>
  <div
    v-if="open"
    class="drawer-layer"
    @mousedown.self="pressedOnBackdrop = true"
    @mouseup.self="closeFromBackdrop"
  >
    <aside
      ref="dialog"
      class="drawer"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
    >
      <header class="drawer-head">
        <div>
          <p class="eyebrow">체험 고객</p>
          <h2 :id="titleId">{{ customer?.name || "체험 고객 상세" }}</h2>
          <a v-if="phoneDigits" class="phone" :href="`tel:${phoneDigits}`">
            {{ prettyPhone(customer?.phone_number) }}
          </a>
        </div>
        <button
          type="button"
          class="icon-btn"
          aria-label="상세 닫기"
          @click="requestClose"
        >
          ×
        </button>
      </header>

      <div v-if="!customer" class="detail-state">
        <template v-if="error">
          <strong>{{ error }}</strong>
          <button type="button" @click="$emit('reload')">다시 시도</button>
        </template>
        <template v-else>상세 정보를 불러오는 중…</template>
      </div>
      <template v-else>
        <div class="snapshot-bar">
          <span class="snapshot">
            <small>방문</small>
            <strong :class="`tone-${visitStatus}`">{{ visitLabel }}</strong>
          </span>
          <span class="snapshot">
            <small>상담</small>
            <strong>{{ consultationLabel }}</strong>
          </span>
          <span class="snapshot">
            <small>등록</small>
            <strong
              :class="{ 'tone-success': customer.registration?.registered }"
            >
              {{ registrationLabel }}
            </strong>
          </span>
        </div>

        <div class="drawer-scroll">
          <p v-if="error" class="banner error" role="alert">
            <span>{{ error }}</span>
            <button type="button" @click="requestReload">
              최신 내용 다시 불러오기
            </button>
          </p>
          <p v-if="customer.request?.failure_reason" class="banner warning">
            예약 처리 메모: {{ customer.request.failure_reason }}
          </p>

          <section class="section">
            <div class="section-head">
              <div>
                <p class="section-kicker">기본 정보</p>
                <h3>어떻게 찾아왔는지</h3>
              </div>
              <label class="compact-field">
                <span>담당 코치</span>
                <select
                  v-model="draft.assigned_coach_id"
                  :disabled="profileBusy"
                >
                  <option value="">미지정</option>
                  <option
                    v-for="coach in coaches"
                    :key="coach.id"
                    :value="coach.id"
                  >
                    {{ coach.name }}
                  </option>
                </select>
              </label>
            </div>

            <div class="field-grid">
              <label class="field">
                <span>처음 알게 된 경로</span>
                <select v-model="draft.inquiry_source" :disabled="profileBusy">
                  <option
                    v-for="source in inquirySources"
                    :key="source.value"
                    :value="source.value"
                  >
                    {{ source.label }}
                  </option>
                </select>
              </label>
              <label class="field">
                <span>문의·예약 경로</span>
                <select v-model="draft.booking_source" :disabled="profileBusy">
                  <option
                    v-for="source in bookingSources"
                    :key="source.value"
                    :value="source.value"
                  >
                    {{ source.label }}
                  </option>
                </select>
              </label>
            </div>

            <fieldset class="fieldset">
              <legend>운동 목적 <small>복수 선택</small></legend>
              <div class="chips">
                <label
                  v-for="goal in goals"
                  :key="goal.value"
                  class="chip"
                  :class="{ checked: draft.goals.includes(goal.value) }"
                >
                  <input
                    v-model="draft.goals"
                    type="checkbox"
                    :value="goal.value"
                    :disabled="profileBusy"
                  />
                  {{ goal.label }}
                </label>
              </div>
            </fieldset>
          </section>

          <section class="section">
            <div class="section-head">
              <div>
                <p class="section-kicker">체험 후 기록</p>
                <h3>반응과 고민을 짧게 기록</h3>
              </div>
              <label class="record-toggle">
                <input
                  v-model="draft.experience_recorded"
                  type="checkbox"
                  :disabled="profileBusy"
                />
                <span>체험 기록 완료</span>
              </label>
            </div>

            <fieldset class="fieldset">
              <legend>운동 강도 반응</legend>
              <div class="chips">
                <label
                  v-for="reaction in intensityReactions"
                  :key="reaction.value"
                  class="chip"
                  :class="{
                    checked: draft.intensity_reaction === reaction.value,
                  }"
                >
                  <input
                    v-model="draft.intensity_reaction"
                    type="radio"
                    name="trial-intensity"
                    :value="reaction.value"
                    :disabled="profileBusy"
                  />
                  {{ reaction.label }}
                </label>
              </div>
            </fieldset>

            <fieldset class="fieldset">
              <legend>좋았던 점 <small>복수 선택</small></legend>
              <div class="chips">
                <label
                  v-for="point in experiencePoints"
                  :key="`positive-${point.value}`"
                  class="chip positive"
                  :class="{
                    checked: draft.positive_points.includes(point.value),
                  }"
                >
                  <input
                    v-model="draft.positive_points"
                    type="checkbox"
                    :value="point.value"
                    :disabled="profileBusy"
                  />
                  {{ point.label }}
                </label>
              </div>
            </fieldset>

            <fieldset class="fieldset">
              <legend>불편했던 점 <small>복수 선택</small></legend>
              <div class="chips">
                <label
                  v-for="point in experiencePoints"
                  :key="`friction-${point.value}`"
                  class="chip negative"
                  :class="{
                    checked: draft.friction_points.includes(point.value),
                  }"
                >
                  <input
                    v-model="draft.friction_points"
                    type="checkbox"
                    :value="point.value"
                    :disabled="profileBusy"
                  />
                  {{ point.label }}
                </label>
              </div>
            </fieldset>

            <fieldset class="fieldset">
              <legend>등록을 망설이는 이유 <small>복수 선택</small></legend>
              <div class="chips">
                <label
                  v-for="reason in objectionReasons"
                  :key="reason.value"
                  class="chip"
                  :class="{
                    checked: draft.objection_reasons.includes(reason.value),
                  }"
                >
                  <input
                    v-model="draft.objection_reasons"
                    type="checkbox"
                    :value="reason.value"
                    :disabled="profileBusy"
                  />
                  {{ reason.label }}
                </label>
              </div>
            </fieldset>

            <div class="note-grid">
              <label class="field">
                <span
                  >고객이 직접 말한 내용
                  <small>{{ draft.customer_comment.length }}/1000</small></span
                >
                <textarea
                  v-model="draft.customer_comment"
                  rows="3"
                  maxlength="1000"
                  placeholder="예: 운동은 좋았지만 퇴근 시간이 걱정돼요"
                  :disabled="profileBusy"
                ></textarea>
              </label>
              <label class="field">
                <span
                  >코치 관찰·판단
                  <small>{{ draft.coach_note.length }}/1000</small></span
                >
                <textarea
                  v-model="draft.coach_note"
                  rows="3"
                  maxlength="1000"
                  placeholder="예: 강도는 잘 따라왔고 저녁반 안내가 필요함"
                  :disabled="profileBusy"
                ></textarea>
              </label>
            </div>
          </section>

          <section class="section">
            <div class="section-head">
              <div>
                <p class="section-kicker">등록 상담</p>
                <h3>다음 행동을 놓치지 않기</h3>
              </div>
            </div>
            <div class="field-grid">
              <label class="field">
                <span>상담 상태</span>
                <select
                  v-model="draft.consultation_status"
                  :disabled="profileBusy"
                >
                  <option
                    v-for="status in consultationStatuses"
                    :key="status.value"
                    :value="status.value"
                  >
                    {{ status.label }}
                  </option>
                </select>
              </label>
              <label class="field">
                <span>다음 연락일</span>
                <input
                  v-model="draft.next_follow_up_at"
                  type="datetime-local"
                  :disabled="
                    profileBusy || draft.consultation_status === 'closed'
                  "
                />
              </label>
            </div>
            <label class="field">
              <span
                >다음 행동
                <small>{{ draft.next_action.length }}/200</small></span
              >
              <input
                v-model="draft.next_action"
                type="text"
                maxlength="200"
                placeholder="예: 목요일 저녁반 일정 확인"
                :disabled="
                  profileBusy || draft.consultation_status === 'closed'
                "
              />
            </label>
            <p v-if="draft.consultation_status === 'closed'" class="help">
              상담을 종료하면 다음 행동과 연락 예정일이 비워집니다. 기존 연락
              이력은 남습니다.
            </p>
          </section>

          <section class="section visit-card">
            <div class="section-head">
              <div>
                <p class="section-kicker">방문·등록</p>
                <h3>운영 기록에서 자동 확인</h3>
              </div>
              <span v-if="visitSummaryText" class="count">{{
                visitSummaryText
              }}</span>
            </div>
            <dl class="facts">
              <div>
                <dt>기준 방문</dt>
                <dd>{{ visitSchedule }}</dd>
              </div>
              <div>
                <dt>방문 상태</dt>
                <dd>{{ visitLabel }}</dd>
              </div>
              <div>
                <dt>유료 등록</dt>
                <dd>{{ registrationLabel }}</dd>
              </div>
            </dl>
            <ol v-if="visits.length" class="visit-list">
              <li
                v-for="(visit, index) in visits"
                :key="`${visit.request_id}-${index}`"
                :class="{ anchored: isAnchorVisit(visit) }"
              >
                <span class="visit-kind">{{ visitKindLabel(visit.kind) }}</span>
                <span class="visit-when">{{ trialVisitWhen(visit) }}</span>
                <span class="visit-title">{{
                  visit.class_title || "수업 미연결"
                }}</span>
                <span class="visit-state" :class="`tone-${visit.status}`">{{
                  visitStatusLabel(visit.status)
                }}</span>
                <span v-if="isAnchorVisit(visit)" class="visit-anchor"
                  >기록 기준</span
                >
              </li>
            </ol>
            <p class="help">
              같은 번호로 다시 방문하면 한 건으로 묶여 이 목록에 쌓입니다. 위의
              담당 코치·체험 기록·다음 행동은 '기록 기준' 방문 하나에 함께
              저장되고, 아래 연락 이력도 이 건 전체의 기록입니다.
            </p>
            <p class="help">
              출석 미체크는 노쇼로 추정하지 않으며, 유료 등록은 코치가 직접
              체크하지 않습니다.
            </p>
            <button
              v-if="canOpenMember"
              type="button"
              class="text-btn"
              @click="$emit('open-member', customer.member_link.member_id)"
            >
              회원 상세 보기 →
            </button>
            <button
              v-if="canBookInquiry"
              type="button"
              class="text-btn"
              @click="requestBooking"
            >
              수업에 예약 연결 →
            </button>
            <p v-else-if="customer.visit?.booking_id" class="help">
              연결된 일정의 변경·취소는 수업 관리에서 처리합니다.
            </p>
          </section>

          <section class="section timeline-section">
            <div class="section-head">
              <div>
                <p class="section-kicker">상담·연락 이력</p>
                <h3>수정하지 않고 계속 쌓기</h3>
              </div>
              <span class="count">{{ customer.contacts.length }}</span>
            </div>

            <ul v-if="customer.contacts.length" class="timeline">
              <li v-for="contact in customer.contacts" :key="contact.id">
                <span class="timeline-dot"></span>
                <div class="timeline-card">
                  <div class="timeline-meta">
                    <strong>{{ contactLabel(contact) }}</strong>
                    <span
                      >{{ shortDateTime(contact.created_at) }} ·
                      {{ contact.author_name || "작성자 미확인" }}</span
                    >
                  </div>
                  <p v-if="contact.note">{{ contact.note }}</p>
                  <small v-if="contact.next_follow_up_at"
                    >다음 연락
                    {{ shortDateTime(contact.next_follow_up_at) }}</small
                  >
                </div>
              </li>
            </ul>
            <p v-else class="empty-inline">아직 연락 기록이 없습니다.</p>

            <form class="contact-form" @submit.prevent="submitContact">
              <div class="field-grid">
                <label class="field">
                  <span>연락 방법</span>
                  <select
                    v-model="contact.channel"
                    :disabled="contactSubmitting"
                  >
                    <option
                      v-for="channel in contactChannels"
                      :key="channel.value"
                      :value="channel.value"
                    >
                      {{ channel.label }}
                    </option>
                  </select>
                </label>
                <label class="field">
                  <span>결과</span>
                  <select
                    v-model="contact.result"
                    :disabled="contactSubmitting"
                  >
                    <option value="" disabled>결과 선택</option>
                    <option
                      v-for="result in contactResults"
                      :key="result.value"
                      :value="result.value"
                    >
                      {{ result.label }}
                    </option>
                  </select>
                </label>
              </div>
              <label class="field">
                <span
                  >연락 내용 <small>{{ contact.note.length }}/1000</small></span
                >
                <textarea
                  v-model="contact.note"
                  rows="3"
                  maxlength="1000"
                  placeholder="통화 내용과 고객의 반응"
                  :disabled="contactSubmitting"
                ></textarea>
              </label>
              <label class="field">
                <span>이 연락 뒤 다음 연락일 <small>선택</small></span>
                <input
                  v-model="contact.next_follow_up_at"
                  type="datetime-local"
                  :disabled="contactSubmitting || contact.result === 'closed'"
                />
              </label>
              <button
                type="submit"
                class="secondary-btn"
                :disabled="contactSubmitting || !contact.result || dirty"
              >
                {{ contactSubmitting ? "기록 중…" : "연락 기록 추가" }}
              </button>
              <p v-if="dirty" class="help form-help">
                위의 고객 정보 변경을 먼저 저장하면 작성 중인 연락 내용은 그대로
                유지됩니다.
              </p>
            </form>
          </section>

          <details v-if="canEditLegacy" class="legacy-section">
            <summary>기존 신청 정보 관리</summary>
            <p class="help">
              기존 신청의 분류·상태·메모만 수정합니다. 수업 예약 일정은 수업
              관리에서 변경합니다.
            </p>
            <div class="field-grid">
              <label class="field">
                <span>종류</span>
                <select v-model="legacy.visit_kind" :disabled="legacySaving">
                  <option value="trial">무료체험</option>
                  <option value="drop_in">드랍인</option>
                </select>
              </label>
              <label class="field">
                <span>기존 상태</span>
                <select
                  v-model="legacy.status"
                  :disabled="legacySaving || !!customer.visit?.booking_id"
                >
                  <option value="pending">대기중</option>
                  <option value="contacted">연락완료</option>
                  <option value="cancelled">예약취소</option>
                </select>
              </label>
            </div>
            <label class="field">
              <span>기존 상담 메모</span>
              <textarea
                v-model="legacy.contact_note"
                rows="3"
                maxlength="1000"
                :disabled="legacySaving"
              ></textarea>
            </label>
            <button
              type="button"
              class="secondary-btn"
              :disabled="legacySaving || !legacyDirty || dirty"
              @click="saveLegacy"
            >
              {{ legacySaving ? "저장 중…" : "기존 정보 저장" }}
            </button>
          </details>
        </div>

        <footer class="drawer-foot">
          <span v-if="dirty" class="dirty-note"
            >저장하지 않은 변경이 있습니다.</span
          >
          <button
            type="button"
            class="ghost-btn"
            :disabled="profileBusy"
            @click="requestClose"
          >
            닫기
          </button>
          <button
            type="button"
            class="primary-btn"
            :disabled="profileBusy || !dirty"
            @click="submitProfile"
          >
            {{ saving ? "저장 중…" : "변경 저장" }}
          </button>
        </footer>
      </template>
    </aside>
  </div>
</template>

<script>
import {
  BOOKING_SOURCES,
  CONSULTATION_STATUSES,
  CONTACT_CHANNELS,
  CONTACT_RESULTS,
  EXPERIENCE_POINTS,
  GOALS,
  INQUIRY_SOURCES,
  INTENSITY_REACTIONS,
  OBJECTION_REASONS,
  buildTrialContact,
  buildTrialUpdate,
  optionLabel,
  prettyPhone,
  shortDateTime,
  trialProfileDraft,
  trialVisitSummaryText,
  trialVisitWhen,
  visitKindLabel,
  visitStatusLabel,
} from "@/lib/trialCRM";

const emptyContact = () => ({
  channel: "phone",
  result: "",
  note: "",
  next_follow_up_at: "",
});

export default {
  name: "TrialCustomerDetail",
  props: {
    open: { type: Boolean, default: false },
    customer: { type: Object, default: null },
    coaches: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
    contactSubmitting: { type: Boolean, default: false },
    legacySaving: { type: Boolean, default: false },
    error: { type: String, default: "" },
    canManageBooking: { type: Boolean, default: false },
    canEditLegacy: { type: Boolean, default: false },
  },
  emits: [
    "close",
    "save",
    "add-contact",
    "open-member",
    "legacy-action",
    "reload",
    "dirty-change",
  ],
  data() {
    return {
      inquirySources: INQUIRY_SOURCES,
      bookingSources: BOOKING_SOURCES,
      goals: GOALS,
      intensityReactions: INTENSITY_REACTIONS,
      experiencePoints: EXPERIENCE_POINTS,
      objectionReasons: OBJECTION_REASONS,
      consultationStatuses: CONSULTATION_STATUSES,
      contactChannels: CONTACT_CHANNELS,
      contactResults: CONTACT_RESULTS,
      draft: trialProfileDraft(),
      baseline: "",
      contact: emptyContact(),
      legacy: { visit_kind: "trial", status: "pending", contact_note: "" },
      legacyBaseline: "",
      pressedOnBackdrop: false,
      focusBeforeOpen: null,
      clearContactOnNextCustomer: false,
      clearLegacyOnNextCustomer: false,
    };
  },
  computed: {
    titleId() {
      return `trial-customer-title-${this.customer?.request_id || "loading"}`;
    },
    phoneDigits() {
      return String(this.customer?.phone_number || "").replace(/\D/g, "");
    },
    visitStatus() {
      return this.customer?.visit?.status || "unknown";
    },
    visitLabel() {
      return visitStatusLabel(this.visitStatus);
    },
    // 에피소드 안 모든 방문이다(신청 행 하나 = 방문 한 번). 서버가 신청 순으로 준다
    visits() {
      return this.customer?.visits || [];
    },
    visitSummaryText() {
      return trialVisitSummaryText(this.customer?.visit_summary);
    },
    consultationLabel() {
      return optionLabel(
        CONSULTATION_STATUSES,
        this.customer?.profile?.consultation_status,
        "상담 전"
      );
    },
    registrationLabel() {
      if (this.customer?.registration?.registered) return "유료 등록";
      if (this.customer?.registration?.existing_member) return "기존 회원";
      return "미등록";
    },
    // 대표 행의 방문이다. 아래 타임라인과 같은 KST 포맷을 써서 한 화면에 두 가지 날짜
    // 표기가 섞이지 않게 한다
    visitSchedule() {
      const visit = this.customer?.visit || {};
      if (!visit.class_date) return "일정 미정";
      return [trialVisitWhen(visit), visit.class_title]
        .filter(Boolean)
        .join(" · ");
    },
    dirty() {
      return !!this.customer && JSON.stringify(this.draft) !== this.baseline;
    },
    contactDirty() {
      return (
        !!this.contact.result ||
        !!this.contact.note.trim() ||
        !!this.contact.next_follow_up_at
      );
    },
    legacyDirty() {
      return JSON.stringify(this.legacy) !== this.legacyBaseline;
    },
    hasDraft() {
      return this.dirty || this.contactDirty || this.legacyDirty;
    },
    profileBusy() {
      return this.saving || this.contactSubmitting || this.legacySaving;
    },
    canOpenMember() {
      return (
        this.canEditLegacy &&
        this.customer?.member_link?.available &&
        this.customer.member_link.member_id
      );
    },
    // 예약 연결은 지금 열려 있는 대표 행(request_id)에 붙는다. 서버도 그 행이 체험이고
    // 살아 있는 예약이 없을 때만 받으므로 조건은 에피소드 전체가 아니라 대표 행의 방문을 본다.
    // 드랍인만 온 손님은 대표 행이 드랍인이어서 버튼이 보이지 않는다
    canBookInquiry() {
      return (
        this.canManageBooking &&
        (this.visitStatus === "unknown" || this.visitStatus === "cancelled") &&
        this.customer?.visit_kind === "trial"
      );
    },
  },
  watch: {
    customer: {
      immediate: true,
      handler(customer, previous) {
        this.resetFromCustomer(customer, previous);
      },
    },
    open(open) {
      if (open) this.openDialog();
    },
    "draft.consultation_status"(status) {
      if (status === "closed") {
        this.draft.next_follow_up_at = "";
        this.draft.next_action = "";
      }
    },
    "contact.result"(result) {
      if (result === "closed") this.contact.next_follow_up_at = "";
    },
    hasDraft(value) {
      this.$emit("dirty-change", value);
    },
  },
  mounted() {
    document.addEventListener("keydown", this.onKeydown);
    window.addEventListener("beforeunload", this.onBeforeUnload);
    if (this.open) this.openDialog();
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    this.restoreFocus();
  },
  methods: {
    prettyPhone,
    shortDateTime,
    trialVisitWhen,
    visitKindLabel,
    visitStatusLabel,
    // 상담 기록·담당·다음 행동이 붙는 대표 행의 방문이다(계약 representative_carries)
    isAnchorVisit(visit) {
      return (
        !!this.customer?.request_id &&
        visit.request_id === this.customer.request_id
      );
    },
    resetFromCustomer(customer, previous = null) {
      const sameCustomer =
        !!customer?.request_id && customer.request_id === previous?.request_id;
      const keepContact =
        sameCustomer && this.contactDirty && !this.clearContactOnNextCustomer;
      const keepLegacy =
        sameCustomer && this.legacyDirty && !this.clearLegacyOnNextCustomer;
      this.draft = trialProfileDraft(customer || {});
      this.baseline = JSON.stringify(this.draft);
      if (!keepContact) this.contact = emptyContact();
      const request = customer?.request || {};
      const nextLegacy = {
        visit_kind: request.visit_kind || customer?.visit_kind || "trial",
        status: request.legacy_status || customer?.legacy_status || "pending",
        contact_note: request.contact_note || "",
      };
      if (!keepLegacy) this.legacy = nextLegacy;
      this.legacyBaseline = JSON.stringify(nextLegacy);
      this.clearContactOnNextCustomer = false;
      this.clearLegacyOnNextCustomer = false;
    },
    openDialog() {
      this.focusBeforeOpen = document.activeElement;
      this.$nextTick(() => this.$refs.dialog?.focus());
    },
    restoreFocus() {
      const element = this.focusBeforeOpen;
      this.focusBeforeOpen = null;
      if (
        element &&
        typeof element.focus === "function" &&
        document.contains(element)
      )
        element.focus();
    },
    hasAnyDraft() {
      return this.hasDraft;
    },
    confirmDiscard() {
      return (
        !this.hasAnyDraft() ||
        window.confirm("저장하지 않은 내용이 있습니다. 닫을까요?")
      );
    },
    requestClose() {
      if (
        this.saving ||
        this.contactSubmitting ||
        this.legacySaving ||
        !this.confirmDiscard()
      )
        return;
      this.restoreFocus();
      this.$emit("close");
    },
    closeFromBackdrop() {
      if (this.pressedOnBackdrop) this.requestClose();
      this.pressedOnBackdrop = false;
    },
    onBeforeUnload(event) {
      if (!this.open || !this.hasAnyDraft()) return;
      event.preventDefault();
      event.returnValue = "";
    },
    onKeydown(event) {
      if (!this.open) return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.requestClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [
        ...(this.$refs.dialog?.querySelectorAll(
          "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary"
        ) || []),
      ];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first ||
          document.activeElement === this.$refs.dialog)
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    submitProfile() {
      if (this.profileBusy || !this.dirty) return;
      // baseline은 상세를 연 때의 draft다. 저장은 부분 수정이라 그와 달라진 칸만 보낸다
      this.$emit(
        "save",
        buildTrialUpdate(
          this.draft,
          this.customer.version,
          JSON.parse(this.baseline)
        )
      );
    },
    submitContact() {
      if (this.contactSubmitting || !this.contact.result || this.dirty) return;
      this.$emit(
        "add-contact",
        buildTrialContact(this.contact, this.customer.version)
      );
    },
    requestBooking() {
      if (!this.confirmDiscard()) return;
      this.$emit("legacy-action", {
        type: "book",
        requestId: this.customer.request_id,
      });
    },
    requestReload() {
      if (!this.confirmDiscard()) return;
      this.$emit("reload");
    },
    saveLegacy() {
      if (this.legacySaving || !this.legacyDirty || this.dirty) return;
      const original = JSON.parse(this.legacyBaseline);
      const payload = { id: this.customer.request_id };
      if (this.legacy.visit_kind !== original.visit_kind) {
        payload.visit_kind = this.legacy.visit_kind;
      }
      if (this.legacy.status !== original.status) {
        payload.status = this.legacy.status;
      }
      if (this.legacy.contact_note !== original.contact_note) {
        payload.contact_note = this.legacy.contact_note.trim();
      }
      this.$emit("legacy-action", {
        type: "update",
        payload,
      });
    },
    acceptContactSaved() {
      this.clearContactOnNextCustomer = true;
    },
    acceptLegacySaved() {
      this.clearLegacyOnNextCustomer = true;
    },
    contactLabel(contact) {
      return `${optionLabel(
        CONTACT_CHANNELS,
        contact.channel,
        "연락"
      )} · ${optionLabel(CONTACT_RESULTS, contact.result, "기록")}`;
    },
  },
};
</script>

<style scoped>
.drawer-layer {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.68);
  backdrop-filter: blur(2px);
}
.drawer {
  width: min(720px, 100vw);
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #111314;
  border-left: 1px solid #303435;
  color: #fff;
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.45);
  outline: none;
}
.drawer-head {
  display: flex;
  flex: none;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #2b2f30;
  background: #151718;
}
.eyebrow,
.section-kicker {
  margin: 0 0 3px;
  color: #78ff47;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.drawer-head h2 {
  margin: 0;
  font-size: 24px;
  line-height: 1.25;
}
.phone {
  display: inline-block;
  margin-top: 5px;
  color: #a7ada9;
  font-size: 13px;
  text-decoration: none;
}
.phone:hover {
  color: #fff;
}
.icon-btn {
  width: 40px;
  height: 40px;
  border: 1px solid #363a3b;
  border-radius: 10px;
  background: #1c1f20;
  color: #dfe2e0;
  font-size: 25px;
  cursor: pointer;
}
.snapshot-bar {
  display: grid;
  flex: none;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid #2b2f30;
  background: #17191a;
}
.snapshot {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 20px;
  border-right: 1px solid #2b2f30;
}
.snapshot:last-child {
  border-right: 0;
}
.snapshot small {
  color: #838a86;
  font-size: 11px;
}
.snapshot strong {
  color: #e6e9e7;
  font-size: 13px;
}
.tone-attended,
.tone-success {
  color: #78ff47 !important;
}
.tone-cancelled {
  color: #ff858b !important;
}
.tone-booked {
  color: #8dbaf2 !important;
}
.drawer-scroll {
  flex: 1;
  overflow: auto;
  padding: 18px 24px 30px;
  overscroll-behavior: contain;
}
.detail-state {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 30px 24px;
  color: #a8aeaa;
}
.detail-state button,
.banner button {
  padding: 6px 9px;
  border: 1px solid currentColor;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  font-size: 11px;
  cursor: pointer;
}
.banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 12px;
  padding: 11px 13px;
  border-radius: 9px;
  font-size: 13px;
}
.banner.error {
  border: 1px solid #713d41;
  background: #311d1f;
  color: #ffb0b4;
}
.banner.warning {
  border: 1px solid #745b2b;
  background: #2b2417;
  color: #f1ce7c;
}
.section {
  margin-bottom: 16px;
  padding: 18px;
  border: 1px solid #292d2e;
  border-radius: 14px;
  background: #17191a;
}
.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.section h3 {
  margin: 0;
  font-size: 17px;
}
.section-kicker {
  color: #888f8b;
}
.field-grid,
.note-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.field,
.compact-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
  color: #bac0bc;
  font-size: 12px;
}
.field > span,
.compact-field > span {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}
.field small,
legend small {
  color: #737a76;
  font-weight: 400;
}
input,
select,
textarea {
  width: 100%;
  border: 1px solid #34393a;
  border-radius: 8px;
  outline: none;
  background: #111314;
  color: #f3f5f4;
  font: inherit;
}
input,
select {
  height: 42px;
  padding: 0 11px;
}
textarea {
  resize: vertical;
  padding: 10px 11px;
  line-height: 1.5;
}
input:focus,
select:focus,
textarea:focus {
  border-color: #78ff47;
  box-shadow: 0 0 0 2px rgba(120, 255, 71, 0.1);
}
input:disabled,
select:disabled,
textarea:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.compact-field {
  width: 170px;
}
.fieldset {
  min-width: 0;
  margin: 16px 0 0;
  padding: 0;
  border: 0;
}
.fieldset legend {
  margin-bottom: 8px;
  color: #bac0bc;
  font-size: 12px;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}
.chip {
  position: relative;
  padding: 7px 10px;
  border: 1px solid #353a3b;
  border-radius: 999px;
  color: #aeb4b0;
  font-size: 12px;
  cursor: pointer;
  user-select: none;
}
.chip input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}
.chip.checked {
  border-color: #78ff47;
  background: rgba(120, 255, 71, 0.1);
  color: #baff9f;
}
.chip.positive.checked {
  border-color: #5d9ad5;
  background: rgba(93, 154, 213, 0.12);
  color: #a9d4ff;
}
.chip.negative.checked {
  border-color: #c68a46;
  background: rgba(198, 138, 70, 0.12);
  color: #f0bf87;
}
.note-grid {
  margin-top: 16px;
}
.record-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #bec4c0;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.record-toggle input {
  width: 17px;
  height: 17px;
  accent-color: #78ff47;
}
.help {
  margin: 9px 0 0;
  color: #7f8682;
  font-size: 12px;
  line-height: 1.5;
}
.facts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin: 0;
  border: 1px solid #2d3132;
  border-radius: 10px;
  overflow: hidden;
}
.facts div {
  padding: 11px;
  border-right: 1px solid #2d3132;
}
.facts div:last-child {
  border: 0;
}
.facts dt {
  color: #7e8581;
  font-size: 11px;
}
.facts dd {
  margin: 4px 0 0;
  font-size: 13px;
}
.visit-list {
  display: grid;
  gap: 6px;
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
}
.visit-list li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  padding: 9px 11px;
  border: 1px solid #2d3132;
  border-radius: 10px;
  background: #111314;
  font-size: 12px;
}
.visit-list li.anchored {
  border-color: #46593c;
}
.visit-kind {
  min-width: 36px;
  color: #cfd4d1;
  font-weight: 700;
}
.visit-when {
  color: #e6e9e7;
}
.visit-title {
  overflow: hidden;
  flex: 1 1 90px;
  min-width: 0;
  color: #949b96;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.visit-state {
  color: #aeb4b0;
}
.visit-anchor {
  padding: 2px 7px;
  border: 1px solid #46593c;
  border-radius: 999px;
  color: #9cff78;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}
.text-btn {
  margin-top: 12px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #78ff47;
  font-size: 13px;
  cursor: pointer;
}
.count {
  min-width: 24px;
  padding: 3px 7px;
  border-radius: 999px;
  background: #262a2b;
  color: #aeb4b0;
  text-align: center;
  font-size: 11px;
}
.timeline {
  margin: 0 0 18px;
  padding: 0;
  list-style: none;
}
.timeline li {
  position: relative;
  padding: 0 0 12px 18px;
  border-left: 1px solid #34393a;
}
.timeline li:last-child {
  padding-bottom: 0;
  border-color: transparent;
}
.timeline-dot {
  position: absolute;
  top: 8px;
  left: -4px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #78ff47;
  box-shadow: 0 0 0 3px #17191a;
}
.timeline-card {
  padding: 11px 12px;
  border-radius: 9px;
  background: #111314;
}
.timeline-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 5px 12px;
}
.timeline-meta strong {
  font-size: 12px;
}
.timeline-meta span,
.timeline-card small {
  color: #7f8682;
  font-size: 11px;
}
.timeline-card p {
  margin: 7px 0 0;
  color: #d3d7d4;
  font-size: 13px;
  white-space: pre-wrap;
}
.empty-inline {
  color: #858c88;
  font-size: 13px;
}
.contact-form {
  display: grid;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #2b2f30;
}
.secondary-btn,
.primary-btn,
.ghost-btn {
  min-height: 42px;
  padding: 0 15px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
}
.secondary-btn {
  justify-self: end;
  border: 1px solid #4b584e;
  background: #202722;
  color: #9cff78;
}
.primary-btn {
  border: 1px solid #78ff47;
  background: #78ff47;
  color: #0a1307;
}
.ghost-btn {
  border: 1px solid #383d3e;
  background: #1a1d1e;
  color: #d9dcda;
}
button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.legacy-section {
  margin-bottom: 16px;
  padding: 16px 18px;
  border: 1px dashed #34393a;
  border-radius: 12px;
  background: #141617;
}
.legacy-section summary {
  color: #aeb4b0;
  font-size: 13px;
  cursor: pointer;
}
.legacy-section .field-grid {
  margin-top: 14px;
}
.legacy-section > .field {
  margin-top: 12px;
}
.legacy-section .secondary-btn {
  margin-top: 12px;
}
.drawer-foot {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  padding: 13px 24px;
  border-top: 1px solid #2b2f30;
  background: #151718;
}
.dirty-note {
  margin-right: auto;
  color: #ddbd72;
  font-size: 11px;
}

@media (max-width: 640px) {
  .drawer {
    width: 100vw;
  }
  .drawer-head {
    padding: 16px;
  }
  .drawer-scroll {
    padding: 12px 12px 24px;
  }
  .snapshot {
    padding: 10px 12px;
  }
  .section {
    padding: 15px;
    border-radius: 12px;
  }
  .section-head {
    flex-direction: column;
  }
  .compact-field {
    width: 100%;
  }
  .field-grid,
  .note-grid,
  .facts {
    grid-template-columns: 1fr;
  }
  .facts div {
    border-right: 0;
    border-bottom: 1px solid #2d3132;
  }
  .drawer-foot {
    padding: 11px 12px;
  }
  .dirty-note {
    display: none;
  }
}
</style>
